import { formatDate } from "../helper/formatDate";
import { eventDateOf } from "../helper/eventFeed";

// Shapes the home feed (helper/eventFeed buildEventFeed) for the public site
// pages: upcoming event cards, results grouped by season and month, and the
// organiser page's crest wall. Dates are calendar days stored as UTC midnight
// (formatDate), as everywhere else in the app.

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MON = MONTHS.map((m) => m.slice(0, 3));
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const PROVINCE = {
  Ontario: "ON", "Québec": "QC", Quebec: "QC", "Nova Scotia": "NS", "Nouvelle-Écosse": "NS",
  "British Columbia": "BC", Alberta: "AB", Manitoba: "MB", Saskatchewan: "SK", "New Brunswick": "NB",
  "Prince Edward Island": "PE", "Newfoundland and Labrador": "NL",
};

const day = (d) => (d ? formatDate(d) : null);
export const shortDate = (d) => { const x = day(d); return x ? `${MON[x.getMonth()]} ${x.getDate()}` : ""; };
export const niceDate = (d) => { const x = day(d); return x ? `${MON[x.getMonth()]} ${x.getDate()}, ${x.getFullYear()}` : ""; };
export const placeOf = (e) => [e?.venue_city, PROVINCE[e?.state] || e?.state].filter(Boolean).join(", ");
export const kindOf = (e) => (e?.eventType === "Oireachtas" ? "Oireachtas" : e?.eventType === "Grade Exam" ? "Grade exam" : "Feis");

// Every day an event runs on: Oireachtas can span several days.
const daysOf = (e) => (e?.eventType === "Oireachtas" && e.eventDays?.length ? e.eventDays : [eventDateOf(e)]).filter(Boolean);

// "Sat, Nov 7, 2026" or "Fri, Nov 20–22, 2026" for a multi-day event.
export const eventDates = (e) => {
  const days = daysOf(e).map(day).sort((a, b) => a - b);
  if (!days.length) return "";
  const a = days[0];
  const b = days[days.length - 1];
  if (a.getTime() === b.getTime()) return `${WEEKDAY[a.getDay()]}, ${MON[a.getMonth()]} ${a.getDate()}, ${a.getFullYear()}`;
  const tail = a.getMonth() === b.getMonth() ? `${b.getDate()}` : `${MON[b.getMonth()]} ${b.getDate()}`;
  return `${WEEKDAY[a.getDay()]}, ${MON[a.getMonth()]} ${a.getDate()}–${tail}, ${b.getFullYear()}`;
};

// Whole days from today until the close date (0 = closes today).
export const daysLeft = (close, now = new Date()) => {
  const c = day(close);
  if (!c) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((c - today) / 86400000);
};

export const daysLeftLabel = (n) => (n === 0 ? "Last day" : n === 1 ? "1 day left" : `${n} days left`);

// The registration line on an upcoming event card.
export const dueLine = (item) => {
  const e = item.event;
  const oir = e.eventType === "Oireachtas";
  if (item.state === "open") return { tone: "open", text: `${oir ? "Teacher entries close" : "Entries close"} ${niceDate(e.closing_date)}`, close: e.closing_date };
  // closed, but still taking late entries (MC2 5a45d7e3): say both
  if (item.state === "late") return { tone: "late", text: `${oir ? "Teacher entries closed" : "Entries closed"} ${shortDate(e.closing_date)} · late entries until ${niceDate(e.lateEntryCloseDate || e.closing_date)}`, close: e.lateEntryCloseDate || e.closing_date };
  if (item.state === "upcoming") return { tone: "soon", text: `Entries open ${niceDate(e.opening_date)}` };
  return { tone: "closed", text: `${oir ? "Teacher entries closed" : "Entries closed"} ${shortDate(e.lateEntryCloseDate || e.closing_date)}` };
};

// A two-day feis is listed as two events ("… - Saturday", "… - Sunday");
// the results list shows it as one card with a link per day.
export const baseName = (name = "") => name
  .replace(/\s*[-:]\s*(Saturday|Sunday)\s*$/i, "")
  .replace(/^(Saturday|Sunday)\s*:\s*/i, "")
  .trim();
export const dayLabel = (name = "") => (name.match(/(Saturday|Sunday)/i) || [])[1] || null;

const yearOf = (e) => { const d = day(eventDateOf(e)); return d ? d.getFullYear() : null; };
const monthOf = (e) => { const d = day(eventDateOf(e)); return d ? d.getMonth() : null; };

// A results card's date range: one feis day, a weekend pair, or an
// Oireachtas's days.
const cardDates = (events) => {
  const days = events.flatMap(daysOf).map(day).sort((a, b) => a - b);
  const a = days[0];
  const b = days[days.length - 1];
  if (a.getTime() === b.getTime()) return `${MON[a.getMonth()]} ${a.getDate()}, ${a.getFullYear()}`;
  const tail = a.getMonth() === b.getMonth() ? `${b.getDate()}` : `${MON[b.getMonth()]} ${b.getDate()}`;
  return `${MON[a.getMonth()]} ${a.getDate()}–${tail}, ${b.getFullYear()}`;
};

// Days of the same feis are at most this far apart.
const SAME_FEIS_DAYS = 2;

const fold = (v) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Past results as seasons, newest first: [{ year, count, months: [{ month,
 * cards: [{ key, name, kind, dates, place, events, search }] }] }].
 * Grade exams have no public results and are left out.
 */
export const groupSeasons = (past = []) => {
  // Days of one feis share a base name and run back to back (a weekend can
  // straddle a month end), so they are paired before months are assigned.
  const groups = [];
  const open = new Map();
  past
    .map((i) => i.event)
    .filter((e) => e && e.eventType !== "Grade Exam" && yearOf(e) !== null)
    .sort((a, b) => day(eventDateOf(a)) - day(eventDateOf(b)))
    .forEach((e) => {
      const name = baseName(e.name);
      const prev = open.get(name);
      const last = prev && day(eventDateOf(prev[prev.length - 1]));
      if (prev && (day(eventDateOf(e)) - last) / 86400000 <= SAME_FEIS_DAYS) {
        prev.push(e);
        return;
      }
      const group = [e];
      groups.push(group);
      open.set(name, group);
    });
  const cards = new Map(groups
    .sort((a, b) => day(eventDateOf(b[0])) - day(eventDateOf(a[0])))
    .map((events) => [`${baseName(events[0].name)}|${events[0]._id}`, events]));

  const seasons = new Map();
  cards.forEach((events, key) => {
    const first = events[0];
    const year = yearOf(first);
    const month = monthOf(first);
    const card = {
      key,
      name: baseName(first.name),
      kind: kindOf(first),
      dates: cardDates(events),
      place: placeOf(first),
      events,
      start: day(eventDateOf(first)),
      search: fold([baseName(first.name), first.venue_city, first.state, PROVINCE[first.state], year, MONTHS[month]].join(" ")),
    };
    if (!seasons.has(year)) seasons.set(year, { year, count: 0, months: new Map() });
    const s = seasons.get(year);
    s.count += events.length;
    if (!s.months.has(month)) s.months.set(month, []);
    s.months.get(month).push(card);
  });

  return [...seasons.values()]
    .sort((a, b) => b.year - a.year)
    .map((s) => ({
      year: s.year,
      count: s.count,
      months: [...s.months.entries()].sort((a, b) => b[0] - a[0]).map(([month, list]) => ({ year: s.year, month, cards: list })),
    }));
};

// The home page's default results view: the last 12 months back from today,
// newest first, whatever the season (so January isn't nearly empty).
export const lastTwelveMonths = (seasons = [], now = new Date()) => {
  const cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  return seasons
    .flatMap((s) => s.months)
    .map((m) => ({ ...m, cards: m.cards.filter((c) => c.start >= cutoff) }))
    .filter((m) => m.cards.length);
};

// Every word of the query must appear (feis name, city, province, year or
// month), accent- and case-insensitive.
export const cardMatches = (card, query) => {
  const words = fold(query).trim().split(/\s+/).filter(Boolean);
  return words.every((w) => card.search.includes(w));
};

// The organiser page's crest wall: one tile per feis that has run on
// Feis.link, newest first, years and weekend days folded together.
export const crestWall = (past = [], limit = 15) => {
  const seen = new Map();
  past
    .map((i) => i.event)
    .filter((e) => e && e.eventType !== "Grade Exam")
    .sort((a, b) => day(eventDateOf(b)) - day(eventDateOf(a)))
    .forEach((e) => {
      const name = baseName(e.name).replace(/\s+(19|20)\d\d$/, "").split(" - ")[0].trim();
      const key = fold(name).replace(/^the\s+/, "");
      if (!name) return;
      // The newest edition names the tile; any edition's crest fills it.
      if (seen.has(key)) {
        const tile = seen.get(key);
        if (!tile.logo && e.logo) tile.logo = e.logo;
        return;
      }
      seen.set(key, { name, logo: e.logo || null });
    });
  return [...seen.values()].slice(0, limit);
};
